import {
    ConditionEditorMode,
    simplifiableCondition,
} from "components/advanced-search/advanced-search-utility";
import { ConditionEditor } from "components/advanced-search/condition-editor";
import { Checkbox } from "components/ui/checkbox";
import { Input } from "components/ui/input";
import {
    AdvancedSearchEntityType,
    AdvancedSearchRequestCondition,
    BridgeEntityResult,
    GetAudienceQuery,
    GetCustomListsQuery,
    useGetEntitiesQuery,
} from "generated/graphql";
import { produce } from "immer";
import React, { useCallback, useState } from "react";
import { useLocation } from "react-router";
import { useCustomLists } from "store/selectors/bridge";
import { useCurrentUserId, useRegionId, useRegions } from "store/selectors/hooks";
import { stableQueryOptions } from "util/requests";
import { AudienceExternalTag } from "./audience-external-tag";
import {
    audienceEntityTypes,
    makeInitialAudienceSearchRequest,
} from "./audience-helpers";
import { getProviderFromPath } from "./audience-provider-utils";
import { AudienceSyncRecent } from "./audience-sync-recent";
import { AudienceSyncStatus } from "./audience-sync-status";

interface AudienceQueryInputProps {
    audience: GetAudienceQuery["getAudience"];
    onChange: (value: GetAudienceQuery["getAudience"]) => void;
    mode: "view" | "edit";
}

export const AudienceQueryInput: React.FC<AudienceQueryInputProps> = props => {
    const { audience, mode, onChange } = props;

    const getEntitiesQuery = useGetEntitiesQuery({}, { ...stableQueryOptions() });
    const regionId = useRegionId();
    const regions = useRegions();
    const { customLists } = useCustomLists();
    const hasMultipleRegions = regions.length > 1;
    const currentUserId = useCurrentUserId();
    const location = useLocation();
    const providerInfo = getProviderFromPath(location.pathname);

    const entities = getEntitiesQuery?.data?.getEntities || [];

    const onEntityTypeAdded = (entityType: AdvancedSearchEntityType) => {
        const advancedSearchRequest = makeInitialAudienceSearchRequest(
            entities,
            entityType,
            regionId,
            hasMultipleRegions,
            regions,
            currentUserId,
            customLists,
        );
        const updated = produce(audience, d => {
            const queriesWithoutThisType = d.query.queries.filter(
                q => q.entityType !== entityType,
            );

            queriesWithoutThisType.push({
                entityType,
                condition: advancedSearchRequest.condition,
                select: advancedSearchRequest.select,
                sort: advancedSearchRequest.sort,
            });

            d.query.queries = queriesWithoutThisType;
        });

        onChange(updated);
    };

    const onConditionChange = (
        entityType: AdvancedSearchEntityType,
        condition: AdvancedSearchRequestCondition,
    ) => {
        const updated = produce(audience, d => {
            const matchingQuery = d.query.queries.find(q => q.entityType === entityType);

            if (matchingQuery) {
                matchingQuery.condition = condition;
            }
        });

        onChange(updated);
    };

    const onItemDelete = (entityType: AdvancedSearchEntityType) => {
        const updated = produce(audience, d => {
            d.query.queries = d.query.queries.filter(q => q.entityType !== entityType);
        });

        onChange(updated);
    };

    const queries = audience.query?.queries || [];

    return (
        <div>
            {mode === "view" ? (
                <div className="space-y-2">
                    <div className="space-y-1">
                        <p className="text-sm">Name</p>
                        <p>{audience.name}</p>
                    </div>
                    <div className="space-y-1">
                        <p className="text-sm">Criteria</p>
                        {queries.length === 0 ? (
                            <p>{`This contact list has no criteria defined yet. Use "Edit criteria" to create a contact list from clients, communities, and referral sources.`}</p>
                        ) : (
                            <>
                                <ul>
                                    {queries
                                        .map(q =>
                                            entities.find(e => e.name === q.entityType),
                                        )
                                        .filter(x => !!x)
                                        .map(e => (
                                            <li key={e.name}>{e.title}</li>
                                        ))}
                                </ul>
                            </>
                        )}
                    </div>
                    {providerInfo.audienceKey &&
                    audience[providerInfo.audienceKey as keyof typeof audience] ? (
                        <AudienceExternalTag
                            title={`${providerInfo.displayName} Tag`}
                            tag={
                                audience[
                                    providerInfo.audienceKey as keyof typeof audience
                                ]?.tag
                            }
                        />
                    ) : null}
                    <AudienceSyncStatus
                        syncResult={
                            audience[providerInfo.audienceKey as keyof typeof audience]
                                ?.latestSyncResult
                        }
                        providerDisplayName={providerInfo.displayName}
                    />
                    <AudienceSyncRecent
                        audienceId={audience.id}
                        regionId={regionId}
                        provider={providerInfo.provider}
                    />
                </div>
            ) : null}
            {mode === "edit" ? (
                <div className="space-y-2">
                    <Input
                        label={<p className="text-sm">Name</p>}
                        value={audience.name}
                        onChange={e => {
                            onChange(
                                produce(audience, d => {
                                    d.name = e.target.value;
                                }),
                            );
                        }}
                    />
                    <div className="space-y-1">
                        <p className="text-sm">Criteria</p>
                        <p>Define criteria for one or more of the record types below</p>
                    </div>
                    <ul className="mt-4">
                        {audienceEntityTypes.map(entityType => {
                            const queryItem =
                                queries.find(q => q.entityType === entityType) || null;

                            return (
                                <li key={entityType}>
                                    <AudienceQueryItem
                                        queryItem={queryItem}
                                        entityType={entityType}
                                        entities={entities}
                                        customLists={customLists}
                                        mode={mode}
                                        onAdd={onEntityTypeAdded}
                                        onConditionChange={onConditionChange}
                                        onDelete={onItemDelete}
                                    />
                                </li>
                            );
                        })}
                    </ul>
                </div>
            ) : null}
        </div>
    );
};

interface AudienceQueryItemProps {
    queryItem: GetAudienceQuery["getAudience"]["query"]["queries"][0];
    entityType: AdvancedSearchEntityType;
    entities: BridgeEntityResult[];
    customLists: GetCustomListsQuery["getCustomLists"];
    mode: "view" | "edit";
    onAdd: (entityType: AdvancedSearchEntityType) => void;
    onConditionChange: (
        entityType: AdvancedSearchEntityType,
        condition: AdvancedSearchRequestCondition,
    ) => void;
    onDelete: (entityType: AdvancedSearchEntityType) => void;
}

const AudienceQueryItem: React.FC<AudienceQueryItemProps> = props => {
    const {
        queryItem,
        entityType,
        entities,
        mode,
        onAdd,
        onConditionChange,
        onDelete,
        customLists,
    } = props;

    const [editorMode, setEditorMode] = useState<ConditionEditorMode>(() => {
        if (!queryItem || !queryItem.condition) {
            return "simple";
        }
        return simplifiableCondition(queryItem.condition) ? "simple" : "advanced";
    });

    const switchAdvanced = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        setEditorMode("advanced");
    }, []);

    const switchSimple = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        setEditorMode("simple");
    }, []);

    const handleCheckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            onAdd(entityType);
        } else {
            onDelete(entityType);
        }
    };

    if (!entities) {
        return null;
    }

    const entityMetadata = entities.find(e => e.name === entityType) || null;

    if (!entityMetadata) {
        return null;
    }

    return mode === "edit" ? (
        <div>
            <div>
                <Checkbox
                    checked={!!queryItem}
                    onChange={handleCheckChange}
                    label={entityMetadata.title}
                />
            </div>
            {queryItem ? (
                <>
                    {editorMode === "advanced" &&
                    simplifiableCondition(queryItem.condition) ? (
                        <a
                            href="#"
                            className="twoverride text-sm text-gray-400"
                            onClick={switchSimple}>
                            Switch to simple mode
                        </a>
                    ) : null}
                    {editorMode === "simple" ? (
                        <a
                            href="#"
                            className="twoverride text-sm text-gray-400"
                            onClick={switchAdvanced}>
                            Switch to advanced mode
                        </a>
                    ) : null}
                    <ConditionEditor
                        entityMetadata={entityMetadata}
                        entities={entities}
                        customLists={customLists}
                        depth={1}
                        mode={editorMode}
                        relativeDepth={1}
                        onChange={c => onConditionChange(entityType, c)}
                        condition={queryItem.condition}
                    />
                </>
            ) : null}
        </div>
    ) : (
        <div>{queryItem.entityType}</div>
    );
};
